import * as React from 'react'
import { IMaskInput } from 'react-imask'
import { cn } from '../../lib/utils'

/**
 * Two masks, picked by what the user starts typing.
 *
 * The Russian one formats as you type, which is worth keeping — it is the
 * overwhelmingly common case and the grouping catches transposed digits. But
 * it used to be the only one, and since it forces `+7`, a foreign number could
 * not be entered at all: the API accepts `+1 555 0100` while the field
 * physically refused it.
 *
 * Anything not starting `+7` falls through to a plain international mask —
 * digits after a `+`, no grouping, because the grouping differs per country
 * and guessing it wrong is worse than not guessing.
 */
const RU_MASK = '+7 (000) 000-00-00'
const INTL_MASK = '+000000000000000'
const PHONE_PLACEHOLDER = '+7 (___) ___-__-__'

const MASKS = [
	{
		mask: RU_MASK,
		/**
		 * `8 999 …` is the trunk-dialling spelling of `+7 999 …`. The mask
		 * renders the `+7` itself, so a leading 8 must be swallowed — otherwise
		 * it lands in the first subscriber slot and shifts every later digit
		 * (a stored `89991234567` became `+7 (899) 912-34-56`). The trade-off
		 * is deliberate: a bare ten-digit city number that itself starts with
		 * 8 loses its first digit, but trunk-style input is what the
		 * placeholders teach and what phone books export.
		 */
		prepare: (chars: string, masked: { unmaskedValue: string }) =>
			masked.unmaskedValue === '' && chars.replace(/\D/g, '').startsWith('8')
				? chars.replace(/\D*8/, '')
				: chars,
	},
	{ mask: INTL_MASK },
]

/**
 * Anything typed WITHOUT `+` is Russian — the same rule the server's
 * normalizePhone applies — so `9161234567` completes to `+7 (916) …` instead
 * of gaining a fabricated `+916` country code. An explicit `+` picks the
 * branch by the country code: only `+7` is the Russian mask; everything else,
 * `+8x` included (Japan/China/Korea — indistinguishable from a stray trunk
 * prefix), is kept verbatim on the international branch, exactly as the
 * server stores it.
 *
 * Returns the index into `MASKS`, which is also the index into the dynamic
 * mask's compiled list — `dispatch` has to hand back a compiled instance, not
 * a literal.
 */
function pickMaskIndex(value: string): number {
	const v = value.trim()
	if (v === '') return 0
	if (v.startsWith('+')) {
		// A lone '+' stays on the free branch: committing to the Russian mask
		// here would rewrite an intended `+1…` into `+7 (1…`.
		return /^\+7/.test(v) ? 0 : 1
	}
	return 0
}

export interface PhoneInputProps {
	/** Cleaned value sent to API, e.g. +79991234567 */
	value: string
	/** Called with cleaned value on every keystroke, e.g. +79991234567 */
	onChange: (value: string) => void
	label?: string
	error?: string
	hint?: string
	id?: string
	disabled?: boolean
	autoFocus?: boolean
	className?: string
	onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
}

const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
	({ value, onChange, label, error, hint, id, disabled, autoFocus, className, onKeyDown }, ref) => {
		const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-')

		return (
			<div className="flex flex-col gap-1.5">
				{label && (
					<label htmlFor={inputId} className="text-sm font-medium text-(--color-neutral-700)">
						{label}
					</label>
				)}
				<IMaskInput
					id={inputId}
					inputRef={ref}
					mask={MASKS}
					dispatch={(appended, dynamicMasked) =>
						dynamicMasked.compiledMasks[
							pickMaskIndex(dynamicMasked.value + appended)
						]
					}
					value={value}
					onAccept={(_masked, maskRef) => {
						// The country code is part of the mask, not of the unmasked
						// value, so it has to be put back. Reading it off the active
						// mask rather than hardcoding +7 is what lets the
						// international branch round-trip.
						const digits = maskRef.unmaskedValue
						if (!digits) {
							onChange('')
							return
						}
						// Which branch is live is read off the formatted value: the
						// Russian mask is the only one that renders a bracket.
						const isRussian = maskRef.value.startsWith('+7 (')
						onChange(isRussian ? `+7${digits}` : `+${digits}`)
					}}
					type="tel"
					inputMode="tel"
					autoComplete="tel"
					autoFocus={autoFocus}
					disabled={disabled}
					placeholder={PHONE_PLACEHOLDER}
					onKeyDown={onKeyDown}
					className={cn(
						'w-full rounded-(--radius) border border-(--color-input) bg-(--color-background) px-4 py-[10px] text-sm leading-(--leading-5) font-normal text-(--color-foreground) placeholder:text-(--color-muted-foreground) transition-colors',
						'focus:outline-none focus:border-(--color-focus) focus:ring-2 focus:ring-(--color-ring)',
						'disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-(--color-muted)',
						error && 'border-(--color-destructive) focus:border-(--color-destructive) focus:ring-(--color-destructive-light)',
						className
					)}
				/>
				{error && <p className="text-xs text-(--color-destructive)">{error}</p>}
				{hint && !error && <p className="text-xs text-(--color-muted-foreground)">{hint}</p>}
			</div>
		)
	}
)

PhoneInput.displayName = 'PhoneInput'

export { PhoneInput }
